import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowRight } from "lucide-react";

const FAQS = [
  {
    q: "Is Generational Health HIPAA-compliant?",
    a: "Yes. HIPAA compliance is built into the platform at every layer — encryption in transit and at rest, access controls, audit trails, and a full BAA workflow with our integrated vendors (pVerify, DoseSpot, Drug Bank, DocuSign). We treat your data like the sensitive health information it is.",
  },
  {
    q: "Is this a mobile app or a website?",
    a: "Generational Health is a mobile app (iOS and Android). This website introduces what we do — the app itself is where you chat with our AI companion, connect with providers, verify insurance, and manage your care.",
  },
  {
    q: "What does the AI companion actually do?",
    a: "Our AI is trained on coping-skill protocols (DBT, CBT, grounding), trauma-informed care frameworks, and crisis triage. It listens, teaches skills, helps you journal, and — critically — knows when to warm-hand you off to a human provider. It's not a replacement for clinical care; it's the bridge to it.",
  },
  {
    q: "How does insurance verification work?",
    a: "Through our pVerify integration, we auto-verify Medicaid, Medicare, and most commercial plans in seconds. You'll know your coverage and estimated out-of-pocket costs before you ever book a session — no paper, no hold music.",
  },
  {
    q: "Can minors use the app?",
    a: "Yes, with appropriate consent. We use DocuSign to handle parental/guardian consent flows, co-signing for minors, and self-consent for emancipated youth. All flows are state-specific and legally compliant.",
  },
  {
    q: "How are prescriptions handled?",
    a: "Licensed providers on the platform prescribe through DoseSpot's certified e-prescribing pipeline. Patients also get plain-language Drug Bank information on every medication — interactions, side effects, and alternatives — right inside the app.",
  },
  {
    q: "What is the 'provider backflow' you mention?",
    a: "It's our patented clinical workflow. Instead of dumping every patient message into a provider's inbox equally, the backflow evaluates urgency, emotional context, and clinical priority — then routes the right signal to the right clinician at the right moment. Less noise, better care.",
  },
  {
    q: "How does Generational Health prevent fraud?",
    a: "Fraud prevention is built into the platform — not bolted on. A real-time integrity monitoring system continuously tracks both patient and provider activity, flags anomalies and suspicious patterns, and creates session-level audit trails for every clinical encounter. Provider credentialing is verified at onboarding and re-verified continuously. The result is a network where bad actors can't operate, insurance partners can trust the data, and good clinicians get paid faster.",
  },
  {
    q: "What does it cost?",
    a: "The app is 100% free to download and use. Crisis support is always free. What's billable are the professional clinical services: sessions with licensed mental health clinicians, peer support services, and group therapy — all billed to insurance (most plans accepted via pVerify) or offered on a sliding scale for the uninsured. Donations fund our access program, which sponsors clinical care for people who can't afford it.",
  },
  {
    q: "Who are the providers?",
    a: "Licensed therapists, counselors, psychiatrists, and psychiatric NPs — all vetted, insured, and credentialed. Peer supporters are certified in peer-support training. Crisis specialists complete 60+ hours of trauma-informed training including QPR and ASIST.",
  },
  {
    q: "Where does my donation go?",
    a: "About 68% funds direct care and app access (sponsoring the app for underserved individuals, provider compensation). 24% funds training and onboarding (peer certification, clinical oversight). 8% covers infrastructure (HIPAA security, technology, a small operations team).",
  },
];

export default function FAQ() {
  const [open, setOpen] = useState(0);

  return (
    <div data-testid="faq-page" className="bg-white">
      <section className="py-20 lg:py-28 bg-gradient-hero" data-testid="faq-hero">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <p className="eyebrow mb-5">Frequently asked</p>
          <h1 className="font-display text-5xl sm:text-6xl text-navy leading-[1.05]">
            The things people <span className="hl-teal">usually ask.</span>
          </h1>
        </div>
      </section>

      <section className="pb-20" data-testid="faq-list-section">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 space-y-3">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className="gh-card !p-0 overflow-hidden" data-testid={`faq-item-${i}`}>
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="w-full flex items-center justify-between text-left p-6 lg:p-7 gap-6 hover:bg-slate-50 transition"
                  data-testid={`faq-toggle-${i}`}
                >
                  <span className="font-display text-lg sm:text-xl text-navy flex-1">{f.q}</span>
                  <ChevronDown className={`w-5 h-5 text-teal flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div className="px-6 lg:px-7 pb-6 lg:pb-7 text-muted-ink leading-relaxed" data-testid={`faq-answer-${i}`}>
                    {f.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section className="py-20 bg-cream" data-testid="faq-cta-section">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-display text-4xl text-navy mb-5 leading-[1.1]">Still have a question?</h2>
          <p className="text-muted-ink mb-8">Our team is here. No question is too small.</p>
          <Link to="/contact" className="btn-primary" data-testid="faq-contact-link">
            Reach out <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
